import React, { useState } from "react";
import styled from "styled-components";

import Language from "@material-ui/icons/Language";
import Person from "@material-ui/icons/Person";
import ExitToApp from "@material-ui/icons/ExitToApp";

import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { Box, Container, Popover } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { Inertia } from "@inertiajs/inertia";
import Alert from "@material-ui/lab/Alert";
import { Trans } from "../../utils/i18n";
import { useLanguageSwitch } from "../../hooks/useLanguageSwitch";
import ClientSwitcher from "../ClientSwitcher";

const Nav = styled.nav`
    width: 100%;
    background-color: ${(props) => props.color || props.theme.primary};
    padding: 15px;
    font-size: 30px;
    box-shadow: 0px 7px 15px -2px rgba(255, 255, 255, 0.6);
`;

const Left = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    padding: 10px;
    flex-wrap: wrap;
`;

const Heading = styled.h1`
    color: ${(props) => props.theme.white};
    display: block;
    margin: 0;
    margin-top: 10px;
    font-size: 15px;
    padding: 6px 10px;
    align-self: flex-center;

    @media (min-width: 800px) {
        font-size: 28px;
        padding: 4px 10px;
        align-self: flex-center;
    }
`;

const Right = styled.div`
    display: flex;
    align-items: center;
`;

const Logo = styled.img`
    width: auto;
    height: 70px;
    margin-right: 10px;
`;

const FooterLogo = styled.img`
    width: auto;
    height: 38px;
    margin-top: 12px;

    &:last-of-type {
        margin-bottom: 0;
    }
`;

const FlashMessage = styled(Alert)`
    margin-top: 30px;
`;

const StyledLanguage = styled(Language)`
    cursor: pointer;
`;

const PopoverContent = styled.div`
    padding: 0 20px;
    border-radius: 10px;
`;

const LanguageSelect = styled.p`
    cursor: pointer;
    color: ${(props) => props.theme.primary};
`;

const ScrollTop = styled.button`
    background-color: #3A4758;
    width: 100%;
    padding: 26px;
    border: none;
    cursor: pointer;
    margin-top: 100px;
    color: ${(props) => props.theme.headericons};
`;

const Footer = styled.footer`
    background-color: ${(props) => props.color || props.theme.primary};
    color: ${(props) => props.theme.headericons};
    font-size: 12px;
    padding: 60px;
`;

const SectionTitle = styled.div`
    color: ${(props) => props.theme.headericons};
    font-size: 16px;
    font-weight: bold;
    padding: 3px;
    text-align: center;
`;

const SectionItem = styled.div`
    color: ${(props) => props.theme.headericons};
    font-size: 14px;
    padding: 4px;
    text-align: center;
    cursor: pointer;
`;

const SectionLink = styled.a`
    color: ${props => props.theme.headericons};
    font-size: 14px;
    padding: 4px;
    text-align: center;
    cursor: pointer;
    text-decoration: none;
`;

const FooterLogos = styled.div`
    background-color: #252F3D;
    color: ${(props) => props.theme.headericons};
    padding: 60px;
    text-align: center;
`;

export default function DefaultLayout({ children, wrapperStyle }) {
    const {
        props: { flash, user, languages },
    } = usePage();
    const { updateLanguage } = useLanguageSwitch();

    const handleLogoutClick = async () => {
        Inertia.post(route("logout"));
    };

    const [langSwitchElement, setLangSwitchElement] = useState();

    const handleLangClick = (event) => {
        setLangSwitchElement(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setLangSwitchElement(null);
    };

    const handleLanguageChange = (language) => {
        setLangSwitchElement(null);
        updateLanguage(language);
    };

    const open = Boolean(langSwitchElement);

    return (
        <>
            <Nav color={"" || children.props.client.brand_color_header}>
                <Container maxWidth="lg">
                    <Grid container spacing={3}>
                        <Left>
                            {children.props.client.brand_logo && (
                                <Logo
                                    src={children.props.client.brand_logo}
                                />
                            )}
                            <Heading
                                style={{
                                    color: children.props.client
                                        .brand_color_header_icons,
                                }}
                            >
                                <Trans i18nKey="layout.oneConnect">
                                    ONE CONNECT
                                </Trans>
                            </Heading>
                        </Left>
                        <Right>
                            <StyledLanguage
                                style={{
                                    color: children.props.client
                                        .brand_color_header_icons,
                                }}
                                onClick={handleLangClick}
                            />
                            <Popover
                                id={open ? "language-popover" : undefined}
                                open={open}
                                anchorEl={langSwitchElement}
                                onClose={handlePopoverClose}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "center",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "center",
                                }}
                            >
                                <PopoverContent>
                                    {languages.map((item) => (
                                        <LanguageSelect
                                            onClick={() =>
                                                handleLanguageChange(item.code)
                                            }
                                        >
                                            { item.name }
                                        </LanguageSelect>
                                    ))}
                                </PopoverContent>
                            </Popover>
                            <ClientSwitcher />
                            {children.props.user?.id && (
                                <InertiaLink
                                    href={route("profile")}
                                    style={{ lineHeight: 0 }}
                                >
                                    <Person
                                        style={{
                                            color: children.props.client
                                                .brand_color_header_icons,
                                        }}
                                    />
                                </InertiaLink>
                            )}
                            {children.props.user?.id && (
                                <ExitToApp
                                    onClick={handleLogoutClick}
                                    style={{
                                        color: children.props.client
                                            .brand_color_header_icons,
                                        cursor: "pointer",
                                    }}
                                />
                            )}
                        </Right>
                    </Grid>
                </Container>
            </Nav>
            <Container maxWidth="lg">
                {flash.message && (
                    <FlashMessage severity="warning">
                        {flash.message}
                    </FlashMessage>
                )}
                {flash.status && (
                    <FlashMessage severity="success">
                        {flash.status}
                    </FlashMessage>
                )}
            </Container>
            <Box paddingY={8} style={wrapperStyle}>{children}</Box>
            <ScrollTop
                onClick={() => {
                    window.scrollTo(0, 0);
                }}
            >
                <Trans i18nKey="layout.backToTop">Back to top</Trans>
            </ScrollTop>
            <FooterLogos>
                <Grid container maxWidth="md" spacing={3} justifyContent="center">
                    <Grid item xs={12} md={3}>
                        <div>
                            An authorized Service of
                        </div>
                        <div>
                            {children.props.client.brand_logo && (
                                <FooterLogo
                                    src={children.props.client.brand_logo}
                                />
                            )}
                        </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <div>
                            Powered by
                        </div>
                        <div>
                            {children.props.client.brand_logo && (
                                <FooterLogo
                                    src={
                                        "/images/logos/b2x.png"
                                    }
                                />
                            )}
                        </div>
                    </Grid>
                </Grid>
            </FooterLogos>
            <Footer color={"" || children.props.client.brand_color_header}>
                <Grid container maxWidth="md" spacing={3} justifyContent="center">
                    <Grid item xs={12} md={3}>
                        <SectionTitle>Section Title</SectionTitle>
                        <SectionItem>Content</SectionItem>
                        <SectionItem>Content</SectionItem>
                        <SectionItem>Content</SectionItem>
                        <SectionItem>Content</SectionItem>
                        <SectionItem>Content</SectionItem>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <SectionTitle>Section Title</SectionTitle>
                        <SectionItem><SectionLink href="/"><Trans i18nKey="layout.sectionItem.welcome">Home</Trans></SectionLink></SectionItem>
                        <SectionItem><SectionLink href="/order"><Trans i18nKey="layout.sectionItem.order">Request a repair</Trans></SectionLink></SectionItem>
                        <SectionItem><SectionLink href="/track"><Trans i18nKey="layout.sectionItem.track">Check status</Trans></SectionLink></SectionItem>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <SectionTitle>Section Title</SectionTitle>
                        <SectionItem>Content</SectionItem>
                        <SectionItem>Content</SectionItem>
                        <SectionItem>Content</SectionItem>
                        <SectionItem><SectionLink href="/login"><Trans i18nKey="layout.sectionItem.agentLogin">Agent Login</Trans></SectionLink></SectionItem>
                    </Grid>
                </Grid>
            </Footer>
        </>
    );
}
